import axios from "axios";

/**
 * Cliente del backend propio (AWS API Gateway).
 *
 * La etapa se llama "UAT" pero es producción: es la misma que consume la app
 * móvil (ver recnet-24/app/config/services.js). Antes esta URL estaba copiada a
 * mano en cada vista del admin, así que cambiarla obligaba a cazar todas las
 * copias.
 */
export const BASE_URL =
  "https://saiteqa7r4.execute-api.us-west-2.amazonaws.com/UAT";

/**
 * Proxy para las operaciones de Stripe.
 *
 * La API de AWS no responde a la verificación previa CORS (`OPTIONS` da 403),
 * así que los POST (tarjetas, alta y baja de suscripción) se bloquean en el
 * navegador. Este proxy —una Cloud Function ya desplegada en el proyecto
 * `reconstruccion-net`, la misma que usa la web de reconstruccion.net— reenvía
 * la petición a AWS de servidor a servidor y sí devuelve cabeceras CORS.
 *
 * Los GET simples funcionan directo contra AWS, pero se enrutan igual por aquí
 * para no depender de que AWS mantenga sus cabeceras.
 */
export const STRIPE_PROXY_URL =
  process.env.VUE_APP_STRIPE_PROXY_URL ||
  "https://us-central1-reconstruccion-net.cloudfunctions.net/stripeProxy";

/**
 * Reintenta sólo lo que vale la pena reintentar: errores de red, 429 y 5xx.
 * Un 404 o un 400 no mejoran por insistir.
 */
export async function withRetries(fn, { tries = 3, baseDelay = 300 } = {}) {
  let lastErr;
  for (let i = 0; i < tries; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const status = err && err.response ? err.response.status : null;
      if (!status || status === 429 || (status >= 500 && status <= 599)) {
        await sleep(baseDelay * Math.pow(2, i));
        continue;
      }
      break;
    }
  }
  throw lastErr;
}

/**
 * Ejecuta `worker` sobre `items` con concurrencia limitada.
 * Sin esto, enriquecer miles de usuarios lanzaba miles de peticiones a la vez.
 */
export async function promisePool(items, worker, limit = 6) {
  const ret = [];
  let idx = 0;
  const runNext = async () => {
    const i = idx++;
    if (i >= items.length) return;
    ret[i] = await worker(items[i], i);
    return runNext();
  };
  const starters = Array(Math.min(limit, items.length)).fill(0).map(runNext);
  await Promise.all(starters);
  return ret;
}

function sleep(ms) {
  return new Promise((res) => setTimeout(res, ms));
}

/**
 * Índice masivo de suscripciones de Stripe.
 * Devuelve el arreglo crudo de `customers`; la normalización vive en
 * `services/suscripciones.js` para que quien sólo quiera los datos no cargue
 * con la interpretación.
 */
export async function getSubscriptionsRaw() {
  const { data } = await withRetries(() =>
    axios.get(`${BASE_URL}/users/subscriptions`, {
      headers: { "Content-Type": "application/json" },
    })
  );
  return Array.isArray(data && data.customers) ? data.customers : [];
}

/** Cliente de Stripe por correo, o null si no existe. */
export async function getCustomerByEmail(email) {
  return withRetries(async () => {
    const { data } = await axios.get(
      `${BASE_URL}/customer/${encodeURIComponent(email)}`
    );
    return (data && data.customer && data.customer.data && data.customer.data[0]) || null;
  });
}

/**
 * Suscripciones de un cliente. La respuesta no tiene una forma estable: según
 * el caso vienen en `active_subscription`, `subscriptions`, `data`, etc.
 */
export async function getSubscriptionsByCustomer(customerId) {
  return withRetries(async () => {
    const { data } = await axios.get(`${BASE_URL}/subscription/${customerId}`);
    return collectSubscriptionsFromResponse(data);
  });
}

export function collectSubscriptionsFromResponse(data) {
  const buckets = [
    data && data.active_subscription,
    data && data.subscriptions,
    data && data.data,
    data && data.all,
    data && data.all_subscriptions,
    data && data.items,
    data && data.results,
  ].filter(Boolean);

  const flat = [];
  for (const b of buckets) {
    if (Array.isArray(b)) {
      for (const s of b) if (s && typeof s === "object") flat.push(s);
    } else if (b && typeof b === "object" && Array.isArray(b.data)) {
      for (const s of b.data) if (s && typeof s === "object") flat.push(s);
    }
  }
  return flat;
}
